import React, { useEffect, useState } from "react";
import { X, Trash2, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED"];

export default function DealForm({
  open,
  onClose,
  deal,
  pipelines,
  defaultPipelineId,
  contacts,
  companies,
  onSaved,
}) {
  const [form, setForm] = useState({
    title: "",
    value: 0,
    currency: "INR",
    pipeline_id: defaultPipelineId || "",
    stage_id: "",
    contact_id: "",
    company_id: "",
    expected_close: "",
    notes: "",
    status: "open",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (deal) {
      setForm({
        title: deal.title || "",
        value: deal.value || 0,
        currency: deal.currency || "INR",
        pipeline_id: deal.pipeline_id || defaultPipelineId || "",
        stage_id: deal.stage_id || "",
        contact_id: deal.contact_id || "",
        company_id: deal.company_id || "",
        expected_close: deal.expected_close || "",
        notes: deal.notes || "",
        status: deal.status || "open",
      });
    } else if (open) {
      setForm((f) => ({
        ...f,
        title: "",
        value: 0,
        contact_id: "",
        company_id: "",
        expected_close: "",
        notes: "",
        status: "open",
        pipeline_id: defaultPipelineId || (pipelines[0]?.id ?? ""),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, open, defaultPipelineId]);

  const activePipeline = pipelines.find((p) => p.id === form.pipeline_id) || pipelines[0];
  const stages = activePipeline?.stages || [];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      title: form.title.trim(),
      value: Number(form.value) || 0,
      currency: form.currency,
      pipeline_id: form.pipeline_id || null,
      stage_id: form.stage_id || null,
      contact_id: form.contact_id || null,
      company_id: form.company_id || null,
      expected_close: form.expected_close || null,
      notes: form.notes || null,
      status: form.status || "open",
    };
    try {
      if (deal) {
        await api.patch(`/crm/deals/${deal.id}`, payload);
        toast.success("Deal updated.");
      } else {
        await api.post("/crm/deals", payload);
        toast.success("Deal created.");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    if (!deal || !window.confirm("Delete this deal?")) return;
    setSubmitting(true);
    try {
      await api.delete(`/crm/deals/${deal.id}`);
      toast.success("Deal deleted.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-[#1A1A1A]/40"
        onClick={submitting ? undefined : onClose}
      />
      <form
        onSubmit={onSubmit}
        data-testid="deal-form"
        className="relative w-full sm:max-w-2xl bg-[#FDFCFB] rounded-t-2xl sm:rounded-2xl border border-[#E8E6E1] shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="px-6 pt-6 pb-4 border-b border-[#E8E6E1] flex items-center justify-between">
          <div>
            <div className="caption mb-1">{deal ? "Edit deal" : "New deal"}</div>
            <h3 className="font-serif-display text-2xl tracking-tight text-[#1A1A1A]">
              {deal ? deal.title : "Create a deal"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded-full hover:bg-[#F5F4F0]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          <div className="sm:col-span-2">
            <label className="caption block mb-1">Title *</label>
            <input
              required
              className="editorial-input"
              value={form.title}
              onChange={set("title")}
              placeholder="e.g. Acme — annual contract"
              data-testid="deal-field-title"
            />
          </div>

          <div>
            <label className="caption block mb-1">Value</label>
            <input
              type="number"
              className="editorial-input"
              value={form.value}
              onChange={set("value")}
              min={0}
              step="any"
              data-testid="deal-field-value"
            />
          </div>
          <div>
            <label className="caption block mb-1">Currency</label>
            <select
              className="editorial-input"
              value={form.currency}
              onChange={set("currency")}
              data-testid="deal-field-currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="caption block mb-1">Pipeline</label>
            <select
              className="editorial-input"
              value={form.pipeline_id}
              onChange={set("pipeline_id")}
            >
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="caption block mb-1">Stage</label>
            <select
              className="editorial-input"
              value={form.stage_id}
              onChange={set("stage_id")}
              data-testid="deal-field-stage"
            >
              <option value="">Auto (first stage)</option>
              {stages.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="caption block mb-1">Contact</label>
            <select
              className="editorial-input"
              value={form.contact_id}
              onChange={set("contact_id")}
            >
              <option value="">—</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="caption block mb-1">Company</label>
            <select
              className="editorial-input"
              value={form.company_id}
              onChange={set("company_id")}
            >
              <option value="">—</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="caption block mb-1">Expected close</label>
            <input
              type="date"
              className="editorial-input"
              value={form.expected_close}
              onChange={set("expected_close")}
            />
          </div>
          <div>
            <label className="caption block mb-1">Status</label>
            <select
              className="editorial-input"
              value={form.status}
              onChange={set("status")}
            >
              <option value="open">Open</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>

          <div className="sm:col-span-2 mt-2">
            <label className="caption block mb-1">Notes</label>
            <textarea
              rows={3}
              className="editorial-input"
              value={form.notes}
              onChange={set("notes")}
              placeholder="Context, next steps, links…"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E8E6E1] flex items-center justify-between gap-3 bg-[#FDFCFB] sticky bottom-0">
          <div>
            {deal && (
              <button
                type="button"
                onClick={onDelete}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 text-sm text-[#B03A2E] hover:underline underline-offset-4"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 text-sm rounded-full hover:bg-[#F5F4F0]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="deal-submit"
              className="px-5 py-2.5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm hover:bg-[#333] disabled:opacity-60 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {deal ? "Save changes" : "Create deal"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
